import React from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { createFilterOptions } from "@mui/material/Autocomplete";

const filter = createFilterOptions();

const SelectField = ({
  name,
  control,
  label,
  options = [],
  loading = false,
  multiple = false,
  disabled = false,
  groupBy,
  valueKey = "id",
  labelKey = "label",
  freeSolo = false, // enable tags mode
  limitTags = 3,
  rules = {},
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={multiple ? [] : null}
      render={({ field, fieldState }) => {
        // Map stored values → UI values
        const selectedValue = multiple
          ? (field.value || []).map((val) => {
              if (typeof val === "string") return val;
              return (
                options.find((opt) => opt[valueKey] === val) || val
              );
            })
          : options.find((opt) => opt[valueKey] === field.value) ||
            field.value ||
            null;

        return (
          <Autocomplete
            multiple={multiple}
            freeSolo={freeSolo}
            options={options}
            groupBy={groupBy}
            loading={loading}
            disabled={disabled}
            value={selectedValue}
            limitTags={limitTags}
            
            // Prevent duplicates
            filterSelectedOptions

            // Add "create new tag"
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;

              const isExisting = options.some(
                (option) =>
                  inputValue ===
                  (typeof option === "string"
                    ? option
                    : option[labelKey])
              );

              if (freeSolo && inputValue !== "" && !isExisting) {
                filtered.push({
                  inputValue,
                  [labelKey]: `Add "${inputValue}"`,
                });
              }

              return filtered;
            }}

            // Label handling
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              if (option.inputValue) return option.inputValue;
              return option[labelKey] || "";
            }}

            // Handle selection
            onChange={(e, value) => {
              if (multiple) {
                field.onChange(
                  value.map((v) => {
                    if (typeof v === "string") return v;
                    if (v.inputValue) return v.inputValue;
                    return v[valueKey];
                  })
                );
              } else {
                const val =
                  typeof value === "string"
                    ? value
                    : value?.inputValue
                    ? value.inputValue
                    : value
                    ? value[valueKey]
                    : null;

                field.onChange(val);
              }
            }}

            // Chip UI
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={
                    typeof option === "string"
                      ? option
                      : option[labelKey]
                  }
                  {...getTagProps({ index })}
                  sx={{
                    background: "#2c2c2c",
                    color: "#fff",
                  }}
                />
              ))
            }

            sx={{
              "& .MuiOutlinedInput-root": {
                background: "transparent",
                color: "#fff",
                padding: "4px 15px",

                "& fieldset": {
                  borderColor: "#444",
                },
                "&:hover fieldset": {
                  borderColor: "#777",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#00bcd4",
                },
                "& .MuiButtonBase-root": {
                  color: "#fff"
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "rgba(255, 255, 255, 0.6)",
                },
              },
            }}

            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={label}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && (
                        <CircularProgress size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

export default SelectField;